import { DbRef } from '@caresend/types';
import { arrayToObj, capitalize, getValidatedUser, formatFullName } from '@caresend/utils';
import update from 'immutability-helper';
import 'firebase/auth';
import { updateUserInfoRequest, createUserRequest } from '../../../database/firebaseConfig/cloudFunctions.js';
import '../../../database/firebaseConfig/init.js';
import { firebaseBind, getValuesOnce, firebaseUnbind } from '../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import { toastSuccess, toastErrorAndReport } from '../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import '../../../router/helpers/initRouter.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { dispatchOrThrow } from '../../helpers.js';
import { initExtendedModule } from '../helpers.js';

const usersState = {
    users: {},
    staffUsers: {},
};
const usersMutations = {
    'users/RESET_STAFF_USERS': (state) => {
        state.staffUsers = {};
    },
    'users/RESET_USERS': (state) => {
        state.users = {};
    },
    'users/SET_STAFF_USERS': (state, { staffUsers, extension, }) => {
        const staffUsersWithOfficeID = staffUsers.map((user) => ({
            ...user,
            ...extension,
        }));
        const staffUsersByID = arrayToObj(staffUsersWithOfficeID, 'id');
        state.staffUsers = update(state.staffUsers, {
            $merge: staffUsersByID,
        });
    },
    'users/SET_USER': (state, newUser) => {
        const userID = newUser.id;
        state.users = update(state.users, {
            [userID]: { $set: newUser },
        });
    },
    'users/SET_STAFF_USER': (state, newUser) => {
        const userID = newUser.id;
        state.staffUsers = update(state.staffUsers, {
            [userID]: { $set: newUser },
        });
    },
    'users/SET_USERS': (state, users) => {
        const usersByID = arrayToObj(users, 'id');
        state.users = update(state.users, {
            $merge: usersByID,
        });
    },
    'users/UNSET_USER': (state, userID) => {
        state.users = update(state.users, {
            $unset: [userID],
        });
    },
};
const usersActions = {
    'users/bindUser': async ({ commit }, userID) => {
        const path = `${DbRef.USERS}/${userID}`;
        await firebaseBind(path, (user) => {
            if (user)
                commit('users/SET_USER', user);
        });
    },
    /** NOTE: This does not work */
    'users/fetchUser': async ({ dispatch }, userID) => {
        await dispatch('users/fetchUsers', [userID]);
    },
    /** NOTE: This does not work due to permission errors */
    'users/fetchUsers': async ({ commit }, userIDs) => {
        const userPaths = userIDs.map((id) => `${DbRef.USERS}/${id}`);
        const users = await getValuesOnce(userPaths);
        commit('users/SET_USERS', users);
    },
    'users/unbindUser': ({ commit }, id) => {
        const path = `${DbRef.USERS}/${id}`;
        firebaseUnbind(path);
        commit('users/UNSET_USER', id);
    },
    async 'users/updateUserInfo'(_context, { updatedUser, hideToast }) {
        try {
            // Not currently supported in patient.
            await dispatchOrThrow(this, 'insurance/createDraftInsuranceProviders');
            await updateUserInfoRequest({ updatedUser });
            if (!hideToast)
                toastSuccess(`${capitalize(updatedUser.role)} updated`);
        }
        catch (error) {
            toastErrorAndReport(error);
        }
    },
    async 'users/createPatientAndAddToOffice'({ rootState }, { draftUser, officeID: inputOfficeID }) {
        var _a, _b;
        const officeID = inputOfficeID !== null && inputOfficeID !== void 0 ? inputOfficeID : (_b = (_a = rootState.office) === null || _a === void 0 ? void 0 : _a.office) === null || _b === void 0 ? void 0 : _b.id;
        if (!officeID)
            throw Error('Missing office ID.');
        try {
            const user = getValidatedUser(draftUser);
            // Not currently supported in patient.
            await dispatchOrThrow(this, 'insurance/createDraftInsuranceProviders');
            // Excluding email link due to product requirements. Email won't be sent when added.
            await createUserRequest({ user });
            // `users` types cannot depend on `offices`, as `offices` already depends
            // on `users`. Use alternative dispatch method.
            await dispatchOrThrow(this, 'offices/addPatientToOffice', {
                officeID,
                patient: user,
            });
            return user.id;
        }
        catch (error) {
            toastErrorAndReport(error);
        }
    },
};
const usersGetters = {
    'users/getStaffUserByID': (state) => (userID) => {
        var _a;
        if (!userID)
            return null;
        return (_a = state.staffUsers[userID]) !== null && _a !== void 0 ? _a : null;
    },
    'users/getUserByID': (state) => (userID) => {
        var _a;
        if (!userID)
            return null;
        return (_a = state.users[userID]) !== null && _a !== void 0 ? _a : null;
    },
    'users/getUserFullName': (state) => (userID) => {
        var _a;
        const user = (_a = state.users[userID]) !== null && _a !== void 0 ? _a : state.staffUsers[userID];
        if (!user)
            return 'Unknown User';
        return formatFullName(user);
    },
};
const usersModule = {
    state: usersState,
    mutations: usersMutations,
    actions: usersActions,
    getters: usersGetters,
};
const initUsersModule = (moduleExtensions) => initExtendedModule(usersModule, moduleExtensions);

export { initUsersModule };
